<template>
  <div v-if="hasWarnings" class="tournament-warnings">
    <!-- Invalid Results Warning -->
    <b-tooltip
      v-if="hasInvalidResults"
      :label="invalidResultMessage"
      position="is-left"
      type="is-danger"
    >
      <span class="danger-icon">⚠️</span>
    </b-tooltip>

    <!-- Repeat Pairing Warning -->
    <b-tooltip
      v-if="hasRepeatPairing"
      label="These players have played before"
      position="is-left"
      type="is-danger"
    >
      <span class="danger-icon">⿻</span>
    </b-tooltip>

    <!-- Color Preference Conflict Warning -->
    <b-tooltip
      v-if="hasColorPreferenceConflict"
      label="Both players prefer the same color"
      position="is-left"
      type="is-danger"
    >
      <span class="warning-icon">🎨</span>
    </b-tooltip>

    <!-- Suboptimal Color Balance Warning -->
    <b-tooltip
      v-if="hasSuboptimalColorBalance"
      :label="colorBalanceMessage"
      position="is-left"
      type="is-danger"
    >
      <span class="warning-icon">⚖️</span>
    </b-tooltip>

    <!-- Repeat Bye Warning -->
    <b-tooltip
      v-if="hasRepeatBye"
      label="Player has had a bye in a previous round"
      position="is-left"
      type="is-danger"
    >
      <span class="warning-icon">ദ</span>
    </b-tooltip>
  </div>
</template>

<script>
import {
  hasInvalidResults,
  getInvalidResultMessage,
} from "vue-chess-shared/tournament/utils/resultUtils";
import {
  getColorPref,
  getPlayedColorPref,
} from "vue-chess-shared/tournament/utils/playerUtils";

export default {
  name: "TournamentGameWarnings",

  props: {
    game: {
      type: Object,
      required: true,
    },
    allGames: {
      type: Array,
      default: () => [],
    },
    round: {
      type: Object,
      required: true,
    },
    findPlayer: {
      type: Function,
      required: true,
    },
  },

  computed: {
    hasInvalidResults() {
      return hasInvalidResults(this.game);
    },

    invalidResultMessage() {
      return getInvalidResultMessage(this.game);
    },

    whitePlayer() {
      if (!this.game.whiteUsername || !this.round.standings) {
        return null;
      }
      return this.round.standings.find(
        (s) => s.player.id === this.game.whiteUsername
      )?.player;
    },

    blackPlayer() {
      if (!this.game.blackUsername || !this.round.standings) {
        return null;
      }
      return this.round.standings.find(
        (s) => s.player.id === this.game.blackUsername
      )?.player;
    },

    hasRepeatPairing() {
      if (
        !this.game.whiteUsername ||
        !this.game.blackUsername ||
        !this.allGames
      ) {
        return false;
      }

      return this.allGames.some(
        (g) =>
          g.roundNumber < this.round.roundNumber &&
          ((g.whiteUsername === this.game.whiteUsername &&
            g.blackUsername === this.game.blackUsername) ||
            (g.whiteUsername === this.game.blackUsername &&
              g.blackUsername === this.game.whiteUsername))
      );
    },

    hasColorPreferenceConflict() {
      if (!this.whitePlayer || !this.blackPlayer) {
        return false;
      }

      const whiteColorPref = getPlayedColorPref(this.whitePlayer);
      const blackColorPref = getPlayedColorPref(this.blackPlayer);

      // Warning if both players prefer the same color
      // Positive colorPref means prefers white, negative means prefers black
      return (
        (whiteColorPref > 0 && blackColorPref > 0) ||
        (whiteColorPref < 0 && blackColorPref < 0)
      );
    },

    hasSuboptimalColorBalance() {
      if (!this.whitePlayer || !this.blackPlayer) {
        return false;
      }

      const whiteColorPref = getPlayedColorPref(this.whitePlayer);
      const blackColorPref = getPlayedColorPref(this.blackPlayer);

      // Calculate current color balance score (lower is better)
      const currentBalance =
        Math.abs(whiteColorPref + 1) + Math.abs(blackColorPref - 1);

      // Calculate swapped color balance score
      const swappedBalance =
        Math.abs(blackColorPref + 1) + Math.abs(whiteColorPref - 1);

      // Warning if swapping would give better balance
      return swappedBalance < currentBalance;
    },

    colorBalanceMessage() {
      if (!this.hasSuboptimalColorBalance) {
        return "";
      }

      const whiteColorPref = getColorPref(this.round, this.whitePlayer);
      const blackColorPref = getColorPref(this.round, this.blackPlayer);

      return `Swapping colors would improve balance: ${whiteColorPref} ⇌ ${blackColorPref}`;
    },

    hasRepeatBye() {
      // Check if this is a bye game (one player missing)
      const isByeGame = !this.game.whiteUsername || !this.game.blackUsername;
      if (!isByeGame) {
        return false;
      }

      // Get the player who has the bye
      const byePlayer = this.game.whiteUsername
        ? this.whitePlayer
        : this.blackPlayer;
      if (!byePlayer || !byePlayer.matches) {
        return false;
      }

      // Check if this player has had a bye in any previous round
      // matches array contains match history with bye: boolean property
      const previousByeCount = byePlayer.matches.filter(
        (match) => match.bye
      ).length;

      // If they have any previous byes, this is a repeat bye
      return previousByeCount > 0;
    },

    hasWarnings() {
      return (
        this.hasInvalidResults ||
        this.hasRepeatPairing ||
        this.hasColorPreferenceConflict ||
        this.hasSuboptimalColorBalance ||
        this.hasRepeatBye
      );
    },
  },
};
</script>

<style scoped>
.tournament-warnings {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.warning-icon,
.danger-icon {
  border-radius: 3px;
  padding: 2px;
  cursor: not-allowed;
  display: inline-block;
  font-size: 0.8rem;
  width: 1.2rem;
}

.warning-icon {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: black;
}

.danger-icon {
  background-color: #f16170;
  border: 1px solid #dc3545;
  color: white;
}
</style>
